'use client';

import { isTruthy } from '@dte/otw-utils-booleans/src/isTruthy';
import {
	AppInsightsContext,
	ReactPlugin,
	useAppInsightsContext,
	withAITracking,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, type IConfig, type IConfiguration } from '@microsoft/applicationinsights-web';
import { type ComponentClass, type PropsWithChildren, type ReactNode, useCallback } from 'react';
import { isProd } from '../../common/util/environmentConfig';
import { filterDependencyTracking } from './filterDependencyTracking';

const connectionString = process.env['NEXT_PUBLIC_APP_INSIGHTS_CONNECTION_STRING'];

let disableTelemetry = false;
// Telemetry should always be enabled in prod
if (!isProd) {
	disableTelemetry = isTruthy(process.env['NEXT_PUBLIC_APP_INSIGHTS_DISABLE_TELEMETRY']);
}

if (!connectionString) {
	disableTelemetry = true;
	console.error('App Insights Telemetry disabled: missing connection string');
} else if (disableTelemetry) {
	console.log('App Insights Telemetry disabled');
}

const config: IConfiguration & IConfig = {
	connectionString: connectionString,

	enableAutoRouteTracking: true,
	enableAjaxPerfTracking: true,
	enableAjaxErrorStatusText: true,
	enableCorsCorrelation: true,
	correlationHeaderDomains: ['*.dteenergy.com'],

	// Monitor all AJAX calls
	maxAjaxCallsPerView: -1,

	// Whether or not to disable telemetry
	disableTelemetry: disableTelemetry,
};

const appInsights = new ApplicationInsights({
	config: config,
});
appInsights.addTelemetryInitializer(filterDependencyTracking);

const reactPlugin = new ReactPlugin();
appInsights.addPlugin(reactPlugin);

appInsights.loadAppInsights();

// Wrapper in case we decide to allow other methods for accessing app insights
export function useAppInsights(): ReactPlugin {
	// TODO: queue up any logs that come in before the plugin is ready
	return useAppInsightsContext();
}

export function useSetAuthenticatedUserContext(): (
	authenticatedUserId: string,
	accountId?: string,
	storeInCookie?: boolean,
) => void {
	// TODO: according to: https://github.com/microsoft/applicationinsights-react-js/issues/19 we should be able to use the following code, but app?.context is coming back as null
	// const app = useAppInsightsContext();
	// const userContext = app?.context?.user;

	// React plugin doesn't expose the interface needed for this, so we need to access it directly
	return useCallback((authenticatedUserId: string, accountId?: string, storeInCookie = true) => {
		if (!appInsights) {
			return;
		}
		appInsights.setAuthenticatedUserContext(authenticatedUserId, accountId, storeInCookie);
	}, []);
}

// To instrument various React components usage tracking, apply the `withAITracking` higher-order
// component function.
const _AppInsightsWrapper = (innerProps: PropsWithChildren): ReactNode => {
	return <AppInsightsContext value={reactPlugin}>{innerProps.children}</AppInsightsContext>;
};
export const AppInsightsWrapper: ComponentClass<{ children?: ReactNode }, unknown> = withAITracking(
	reactPlugin,
	_AppInsightsWrapper,
);
